import { useEffect, useState } from 'react';

export type GeoStatus = 'checking' | 'allowed' | 'blocked';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Only enforce on the live domain, so previews and local work are never blocked. */
const ENFORCED_HOST = 'fulldetailpro.com';
/** Free, keyless, CORS-enabled IP lookup. Returns approximate country/region only. */
const GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/geo.json';
const CACHE_KEY = 'mpd_geo_status';
const TIMEOUT_MS = 3000;
/** Search engines, social previews, and audit tools always get the full site. */
const CRAWLER_PATTERN = /bot|crawl|spider|slurp|mediapartners|adsbot|google-inspectiontool|bingpreview|duckduck|baidu|yandex|facebookexternalhit|twitterbot|linkedinbot|embedly|lighthouse|pagespeed|gtmetrix|headless/i;

interface GeoResponse {
  country_code?: string;
  region?: string;
}

/**
 * Approximate California-only access check.
 * Fails open: if the lookup errors, times out, or returns no region, the
 * visitor is allowed. Only the region name is used — the IP is never stored
 * or displayed.
 */
export function useCaliforniaGate(): GeoStatus {
  const [status, setStatus] = useState<GeoStatus>(() => {
    if (typeof window === 'undefined') return 'allowed';
    if (!window.location.hostname.endsWith(ENFORCED_HOST)) return 'allowed';
    if (CRAWLER_PATTERN.test(navigator.userAgent)) return 'allowed';
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached === 'allowed' || cached === 'blocked') return cached;
    return 'checking';
  });

  useEffect(() => {
    if (status !== 'checking') return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

    fetch(GEO_ENDPOINT, {
      signal: controller.signal
    }).then(res => res.ok ? res.json() as Promise<GeoResponse> : Promise.reject()).then(geo => {
      const country = geo.country_code?.toUpperCase();
      const region = geo.region?.trim();
      // Undetermined location → allow.
      if (!country || !region) {
        finish('allowed', 'unknown', undefined);
        return;
      }
      const inCalifornia = country === 'US' && region.toLowerCase() === 'california';
      finish(inCalifornia ? 'allowed' : 'blocked', inCalifornia ? 'california' : 'outside_california', country === 'US' ? region : country);
    }).catch(() => finish('allowed', 'unknown', undefined)).finally(() => window.clearTimeout(timer));

    function finish(next: 'allowed' | 'blocked', geoStatus: string, geoArea: string | undefined) {
      sessionStorage.setItem(CACHE_KEY, next);
      window.gtag?.('event', 'geo_check', {
        geo_status: geoStatus,
        geo_area: geoArea ?? 'unknown'
      });
      setStatus(next);
    }

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [status]);

  return status;
}
